/**
 * Learn — public educational video library.
 *
 * PUBLIC VIEW (everyone):
 *   Hero → category-grouped grid of published videos → click to play.
 *
 * EDITOR VIEW (users with `learn_edit` permission):
 *   Small "Manage" button at bottom-right → PIN dialog → shows an
 *   "Add video" bar + per-card Edit/Delete/Publish controls.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Play,
  Plus,
  Settings,
  Pencil,
  Trash2,
  LogOut,
  EyeOff,
  Eye,
  BookOpen,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  listCategories,
  listVideos,
  deleteVideo,
  updateVideo,
  getToken,
  clearToken,
  youTubeThumb,
  formatDuration,
  streamUrlFor,
  isYouTubeUrl,
  isVimeoUrl,
} from "@/lib/learn/api";
import VideoPlayer from "@/pages/learn/VideoPlayer";
import PinDialog from "@/pages/learn/PinDialog";
import VideoEditor from "@/pages/learn/VideoEditor";

export default function Learn() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").toLowerCase().startsWith("fr") ? "fr" : "en";
  const [categories, setCategories] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(() => !!getToken());
  const [showPin, setShowPin] = useState(false);
  const [playing, setPlaying] = useState(null);       // video record
  const [editing, setEditing] = useState(null);       // "new" | video record
  const [busyId, setBusyId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [c, v] = await Promise.all([
        listCategories(),
        listVideos({ includeUnpublished: editMode, language: lang }),
      ]);
      setCategories(c);
      setVideos(v);
    } catch (err) {
      toast.error("Failed to load videos");
    } finally {
      setLoading(false);
    }
  }, [editMode, lang]);

  useEffect(() => { reload(); }, [reload]);

  // Group + sort videos by category
  const grouped = useMemo(() => {
    const localLabel = (c) => (lang === "fr" && c.label_fr) ? c.label_fr : c.label;
    const byCat = new Map();
    for (const c of categories) byCat.set(c.slug, { ...c, label: localLabel(c), items: [] });
    for (const v of videos) {
      if (!byCat.has(v.category)) byCat.set(v.category, { slug: v.category, label: v.category, items: [] });
      byCat.get(v.category).items.push(v);
    }
    // Sort items by order, then created_at
    for (const g of byCat.values()) {
      g.items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || String(a.created_at).localeCompare(String(b.created_at)));
    }
    return Array.from(byCat.values()).filter((g) => g.items.length > 0 || editMode);
  }, [categories, videos, editMode, lang]);

  const featured = useMemo(
    () => videos.find((v) => v.published) || videos[0] || null,
    [videos],
  );

  const onDelete = async (v) => {
    if (!window.confirm(`Delete "${v.title}"? This cannot be undone.`)) return;
    setBusyId(v.id);
    try {
      await deleteVideo(v.id);
      setVideos((cur) => cur.filter((x) => x.id !== v.id));
      toast.success("Video removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const onTogglePublished = async (v) => {
    setBusyId(v.id);
    try {
      const saved = await updateVideo(v.id, { published: !v.published });
      setVideos((cur) => cur.map((x) => (x.id === v.id ? saved : x)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const onSignOut = () => {
    clearToken();
    setEditMode(false);
    toast.success("Signed out");
  };

  return (
    <div className="min-h-screen bg-brand-offwhite pt-20 pb-24" data-testid="learn-page">
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="h-5 w-5 text-brand-teal" />
          <span className="text-xs font-semibold uppercase tracking-[0.24em] text-brand-teal">
            {t("learn.hero.kicker")}
          </span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-5xl font-bold font-heading text-brand-navy leading-tight max-w-3xl lg:max-w-none lg:whitespace-nowrap">
          {t("learn.hero.title")}
        </h1>
        <p className="mt-4 text-base sm:text-lg text-slate-500 max-w-2xl">
          {t("learn.hero.subtitle")}
        </p>

        {featured && (
          <button
            type="button"
            onClick={() => setPlaying(featured)}
            className="mt-10 group block w-full text-left"
            data-testid="learn-featured"
          >
            <div className="relative aspect-video rounded-2xl overflow-hidden bg-brand-navy shadow-xl">
              <VideoThumb video={featured} className="absolute inset-0 h-full w-full" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-white/95 group-hover:scale-110 group-hover:bg-white transition flex items-center justify-center shadow-lg">
                  <Play className="h-7 w-7 sm:h-8 sm:w-8 text-brand-navy ml-1" fill="currentColor" />
                </span>
              </div>
              <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 text-white">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] opacity-80">
                  {t("learn.hero.featured")}
                </p>
                <h2 className="mt-1 text-xl sm:text-2xl font-bold font-heading leading-tight">
                  {featured.title}
                </h2>
              </div>
            </div>
          </button>
        )}
      </section>

      {/* Category grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-14 sm:mt-20">
        {loading && (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            {t("learn.loading")}
          </div>
        )}

        {!loading && videos.length === 0 && (
          <EmptyState editMode={editMode} onAdd={() => setEditing("new")} />
        )}

        {!loading && grouped.map((g) => (
          <div key={g.slug} className="mb-14" data-testid={`learn-category-${g.slug}`}>
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="text-xl sm:text-2xl font-bold font-heading text-brand-navy">
                {g.label}
              </h3>
              <span className="text-sm text-slate-400">{g.items.length} {g.items.length === 1 ? t("learn.videosSuffix") : t("learn.videosSuffixPlural")}</span>
            </div>
            {g.items.length === 0 ? (
              <p className="text-sm text-slate-400 italic">{t("learn.emptyCategory")}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {g.items.map((v) => (
                  <VideoCard
                    key={v.id}
                    video={v}
                    editMode={editMode}
                    busy={busyId === v.id}
                    onPlay={() => setPlaying(v)}
                    onEdit={() => setEditing(v)}
                    onDelete={() => onDelete(v)}
                    onTogglePublished={() => onTogglePublished(v)}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </section>

      {/* Floating admin controls */}
      <div className="fixed bottom-6 left-6 z-[60] flex items-center gap-2">
        {editMode ? (
          <>
            <button
              type="button"
              onClick={() => setEditing("new")}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white font-semibold shadow-lg transition"
              data-testid="learn-add-video"
            >
              <Plus className="h-4 w-4" />
              Add video
            </button>
            <button
              type="button"
              onClick={onSignOut}
              className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-white hover:bg-slate-50 text-brand-navy font-semibold shadow-lg border border-slate-200 transition"
              data-testid="learn-signout"
              title="Sign out of edit mode"
            >
              <LogOut className="h-4 w-4" />
              Exit
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setShowPin(true)}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white/95 hover:bg-white text-brand-navy font-semibold shadow-lg border border-slate-200 transition"
            data-testid="learn-manage"
          >
            <Settings className="h-4 w-4" />
            Manage
          </button>
        )}
      </div>

      {/* Modals */}
      {showPin && (
        <PinDialog
          onClose={() => setShowPin(false)}
          onAuthed={() => {
            setShowPin(false);
            setEditMode(true);
            toast.success("Editor mode unlocked");
          }}
        />
      )}
      {playing && <VideoPlayer video={playing} onClose={() => setPlaying(null)} />}
      {editing && (
        <VideoEditor
          initial={editing === "new" ? null : editing}
          categories={categories}
          defaultLanguage={lang}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            setVideos((cur) => {
              // Drop videos that no longer belong on this language view.
              const belongs = (saved.language || "en") === lang;
              const idx = cur.findIndex((x) => x.id === saved.id);
              if (idx === -1) return belongs ? [...cur, saved] : cur;
              const next = [...cur];
              if (belongs) next[idx] = saved;
              else next.splice(idx, 1);
              return next;
            });
          }}
        />
      )}
    </div>
  );
}

// ------------------------------- Sub-components ------------------------------

function VideoThumb({ video, className = "" }) {
  const explicit = video.thumbnail_url || youTubeThumb(video.external_url || "");
  // For self-hosted uploads and direct MP4 links (non-YT / non-Vimeo) we
  // can grab the frame at the video's chosen poster_time_seconds (default 2s)
  // client-side and use it as the poster.
  const canGrabFrame =
    !explicit &&
    (
      !!video.storage_path ||
      (video.external_url && !isYouTubeUrl(video.external_url) && !isVimeoUrl(video.external_url))
    );
  const frameSrc = video.storage_path
    ? streamUrlFor(video.id)
    : (canGrabFrame ? video.external_url : "");
  const posterTime = Number.isFinite(Number(video.poster_time_seconds))
    ? Number(video.poster_time_seconds)
    : 2;
  const grabbed = useVideoFrameThumb(canGrabFrame ? frameSrc : "", posterTime);

  const src = explicit || grabbed;
  if (src) {
    return (
      <img
        src={src}
        alt={video.title}
        className={`${className} object-cover`}
        loading="lazy"
        draggable={false}
      />
    );
  }
  return (
    <div className={`${className} bg-gradient-to-br from-brand-navy to-brand-teal flex items-center justify-center`}>
      <Play className="h-10 w-10 text-white/60" fill="currentColor" />
    </div>
  );
}

// Cache of extracted frames keyed by source URL so we don't repeatedly
// load a hidden <video> element every time a card re-renders.
const _frameCache = new Map();

/**
 * Extract a still frame from a video URL at `atSeconds` using an off-DOM
 * <video>+<canvas>. Returns a data-URL string once ready, or "".
 * Works for same-origin streamed videos (our /api/learn/stream/*) and
 * any CORS-permitted direct MP4 links. Silently falls back to empty
 * for CORS-tainted sources so the caller shows the play-icon poster.
 */
function useVideoFrameThumb(src, atSeconds = 2) {
  const cacheKey = src ? `${src}#t=${atSeconds}` : "";
  const [dataUrl, setDataUrl] = useState(() => (cacheKey ? _frameCache.get(cacheKey) || "" : ""));
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (!src) { setDataUrl(""); return; }
    const cached = _frameCache.get(cacheKey);
    if (cached) { setDataUrl(cached); return; }

    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    // iOS Safari won't fire `seeked` on a source unless the element is
    // in the DOM. Attach off-screen.
    video.style.cssText = "position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0;pointer-events:none";
    document.body.appendChild(video);

    const cleanup = () => {
      try { video.pause(); } catch { /* noop */ }
      video.removeAttribute("src");
      try { video.load(); } catch { /* noop */ }
      if (video.parentNode) video.parentNode.removeChild(video);
    };

    const onSeeked = () => {
      if (cancelledRef.current) { cleanup(); return; }
      try {
        const w = video.videoWidth || 640;
        const h = video.videoHeight || 360;
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, w, h);
        const url = canvas.toDataURL("image/jpeg", 0.75);
        _frameCache.set(cacheKey, url);
        if (!cancelledRef.current) setDataUrl(url);
      } catch {
        // toDataURL throws for CORS-tainted canvases — fall back silently.
      } finally {
        cleanup();
      }
    };

    const onLoaded = () => {
      const dur = video.duration || 0;
      const clamped = Math.max(
        0,
        Math.min(Number(atSeconds) || 0, Math.max(0, dur - 0.1)),
      );
      try { video.currentTime = clamped; } catch { cleanup(); }
    };

    const onError = () => cleanup();

    video.addEventListener("loadedmetadata", onLoaded);
    video.addEventListener("seeked", onSeeked, { once: true });
    video.addEventListener("error", onError, { once: true });
    video.src = src;

    return () => {
      cancelledRef.current = true;
      video.removeEventListener("loadedmetadata", onLoaded);
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("error", onError);
      cleanup();
    };
  }, [src, atSeconds, cacheKey]);

  return dataUrl;
}

function VideoCard({ video, editMode, busy, onPlay, onEdit, onDelete, onTogglePublished }) {
  const duration = formatDuration(video.duration_seconds);
  return (
    <div
      className="group relative rounded-2xl overflow-hidden bg-white border border-slate-100 shadow-sm hover:shadow-lg hover:border-brand-teal/40 transition-all"
      data-testid={`learn-card-${video.id}`}
    >
      <button
        type="button"
        onClick={onPlay}
        className="block w-full text-left"
        aria-label={`Play ${video.title}`}
      >
        <div className="relative aspect-video bg-slate-100">
          <VideoThumb video={video} className="absolute inset-0 h-full w-full" />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="h-12 w-12 rounded-full bg-white/95 group-hover:scale-110 transition flex items-center justify-center shadow-md">
              <Play className="h-5 w-5 text-brand-navy ml-0.5" fill="currentColor" />
            </span>
          </span>
          {duration && (
            <span className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded bg-black/70 text-white text-xs font-medium tabular-nums">
              {duration}
            </span>
          )}
          {editMode && !video.published && (
            <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-amber-500/95 text-white text-[10px] font-bold uppercase tracking-wider">
              Draft
            </span>
          )}
        </div>
        <div className="p-4">
          <h4 className="text-base font-semibold text-brand-navy leading-snug line-clamp-2">
            {video.title}
          </h4>
          {video.description && (
            <p className="mt-1 text-sm text-slate-500 line-clamp-2">
              {video.description}
            </p>
          )}
        </div>
      </button>

      {editMode && (
        <div className="border-t border-slate-100 px-3 py-2 flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={onTogglePublished}
            disabled={busy}
            className="h-8 w-8 rounded-md text-slate-500 hover:text-brand-navy hover:bg-slate-100 flex items-center justify-center transition disabled:opacity-50"
            title={video.published ? "Unpublish (hide from public)" : "Publish"}
            data-testid={`learn-card-toggle-${video.id}`}
          >
            {video.published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={onEdit}
            disabled={busy}
            className="h-8 w-8 rounded-md text-slate-500 hover:text-brand-navy hover:bg-slate-100 flex items-center justify-center transition disabled:opacity-50"
            title="Edit"
            data-testid={`learn-card-edit-${video.id}`}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={busy}
            className="h-8 w-8 rounded-md text-red-500 hover:text-white hover:bg-red-500 flex items-center justify-center transition disabled:opacity-50"
            title="Delete"
            data-testid={`learn-card-delete-${video.id}`}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </button>
        </div>
      )}
    </div>
  );
}

function EmptyState({ editMode, onAdd }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white/60 p-10 text-center" data-testid="learn-empty">
      <div className="h-14 w-14 rounded-full bg-brand-mist mx-auto flex items-center justify-center text-brand-navy">
        <BookOpen className="h-6 w-6" />
      </div>
      <h3 className="mt-4 text-xl font-bold font-heading text-brand-navy">
        The library is just getting started
      </h3>
      <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
        {editMode
          ? "Upload your first video or paste a YouTube / Vimeo link to bring this page to life."
          : "Check back soon — our team is filming a series of educational shorts on Hepatitis C care."}
      </p>
      {editMode && (
        <button
          type="button"
          onClick={onAdd}
          className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white font-semibold shadow-sm transition"
          data-testid="learn-empty-add"
        >
          <Plus className="h-4 w-4" />
          Add the first video
        </button>
      )}
    </div>
  );
}
